import React from "react";
import { Camera, History, Move, Package, Wrench } from "lucide-react";
import { fileUrl } from "../../lib/api";
import { BulkCheckbox } from "./BulkSelectContext";
import { ITEM_TYPES } from "./constants";

export default function ItemCard({ it, meta, onMove, onEdit, onHistory, onRepair, onIssue, onPhotos }) {
  const colorObj = meta.colors.find((c) => c.code === it.color);
  const defect = it.status === "defect";
  const statusBg = defect ? "#EF4444" : "#10B981";
  const canIssue = !defect && (it.item_type === "piesa_auto" || it.item_type === "consumabil") && (parseFloat(it.quantity) || 0) > 0;
  const photoIds = it.photo_ids || [];
  const firstPhoto = photoIds[0];
  const thumbUrl = firstPhoto ? fileUrl(firstPhoto) : null;
  const missingSerial = !it.serial && !it.no_serial;
  return (
    <div className={`relative border rounded-md p-3 flex flex-col gap-1.5 hover:border-[var(--primary)] transition-colors group ${missingSerial ? "border-amber-400 bg-amber-50/40 ring-1 ring-amber-200" : (defect ? "border-[var(--red-bila)]" : "border-[var(--border)]")}`} data-testid={`item-${it.id}`}>
      <BulkCheckbox id={it.id} className="absolute top-1.5 left-1.5 z-10"/>
      {missingSerial && (
        <div className="bg-amber-400 text-amber-900 -mx-3 -mt-3 mb-1 px-3 py-1.5 text-[11px] font-extrabold flex items-center gap-1.5 rounded-t-md" data-testid={`item-missing-serial-banner-${it.id}`}>
          <span className="text-base leading-none">⚠️</span>
          <span>LIPSĂ NR. SERIE — Editează pentru a completa</span>
        </div>
      )}
      <div className="flex items-start gap-2">
        <span className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ background: statusBg }} title={defect ? "Defect" : "Funcțional"}/>
        {/* Thumbnail / icon — click opens gallery */}
        {onPhotos && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPhotos(); }}
            className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0 bg-zinc-100 border border-zinc-200 hover:border-[var(--primary)] transition-colors flex items-center justify-center relative"
            title={photoIds.length ? `${photoIds.length} poze` : "Adaugă poze"}
            data-testid={`item-thumb-${it.id}`}
          >
            {thumbUrl ? (
              <img src={thumbUrl} alt={it.name} className="w-full h-full object-cover"/>
            ) : (
              <Package size={18} className="text-zinc-400"/>
            )}
            {photoIds.length > 1 && (
              <span className="absolute -top-1 -right-1 bg-[var(--primary)] text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {photoIds.length}
              </span>
            )}
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate" title={it.name}>{it.name}{it.is_oil ? " 🛢️" : ""}</div>
          {it.serial && (
            <div
              className="text-[10px] text-zinc-600 font-mono truncate mt-0.5"
              title={`Serie: ${it.serial}`}
              data-testid={`item-serial-${it.id}`}
            >
              SN: {it.serial}
            </div>
          )}
          {!it.serial && it.no_serial && (
            <div className="text-[10px] text-zinc-400 italic truncate">fără serie</div>
          )}
          {!it.serial && !it.no_serial && (
            <div className="text-[9px] mt-0.5 px-1 py-0.5 rounded bg-amber-100 text-amber-800 font-bold border border-amber-300 inline-block" title="Lipsă nr. serie" data-testid={`item-missing-serial-card-${it.id}`}>
              ⚠️ Lipsă SN
            </div>
          )}
          <div className="text-[10px] text-zinc-500">{ITEM_TYPES.find((t) => t.code === it.item_type)?.label || it.item_type}{it.quantity > 1 ? ` · ${it.quantity}${it.unit ? " " + it.unit : ""}` : ""}</div>
          {it.supplier && (
            <div className="text-[10px] text-blue-700 truncate mt-0.5" title={`Furnizor: ${it.supplier}`}>
              🏭 {it.supplier}
            </div>
          )}
        </div>
        {colorObj && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1" style={{ background: colorObj.hex }} title={`Culoare: ${colorObj.label}`}/>}
      </div>
      {(it.shelf_col || it.shelf_row) && (
        <div className="text-[10px] text-zinc-600 font-mono">📍 {it.shelf_row || "-"}{it.shelf_col || ""}</div>
      )}
      <div className="flex flex-wrap gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {defect && it.item_type === "echipament" && onRepair && (
          <button onClick={onRepair} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--green-bg)] text-[var(--green-valid)] hover:opacity-80 font-bold" title="Repară">
            <Wrench size={10} className="inline"/> REPARĂ
          </button>
        )}
        {canIssue && onIssue && (
          <button onClick={onIssue} className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#B45309] hover:opacity-80 font-bold" title="Preia din magazie" data-testid={`issue-btn-${it.id}`}>
            <Package size={10} className="inline"/> Preia
          </button>
        )}
        {onPhotos && (
          <button onClick={onPhotos} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--primary-subtle)] text-[var(--primary)] hover:opacity-80" title="Poze & partajare" data-testid={`photos-btn-${it.id}`}>
            <Camera size={10} className="inline"/> Poze
          </button>
        )}
        <button onClick={onMove} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--primary-subtle)] text-[var(--primary)] hover:opacity-80" title="Mută"><Move size={10} className="inline"/> Mută</button>
        <button onClick={onEdit} className="text-[10px] text-zinc-600 hover:text-zinc-900">Editează</button>
        <button onClick={onHistory} className="text-[10px] text-zinc-600 hover:text-zinc-900" title="Istoric"><History size={10} className="inline"/></button>
      </div>
    </div>
  );
}
